import { Helper } from '@/mixin/helper'
import { isValidImageUrl } from '@/helpers/is-valid-image-url'
import { GET_RAW_ALERTS_VIDEO_HISTORY } from '@/services/api/videoHistory/alert'
import {
  GET_VEHICLE_VIDEOS_TV2,
  GET_THUMBNAIL_FOR_VIDEO_HISTORY,
} from '@/services/api/videoHistory/videos'
import { client } from '@/services/apolloclient'
import Vue from 'vue'
import { UPDATE_FILELIST, SET_UPLOAD_MEDIA } from '@/services/api/onlineCommand'
import { mcToast } from '@/plugins/McToastAlertPlugin'
import i18n from '@/i18n'
import moment from 'moment'
moment.locale('id')

const streamIframes = new Map()
const getStreamKey = (imei, channel) => `${imei}-${channel}`

const mergeFilelistIntervals = (filelist = []) => {
  const intervals = filelist
    .reduce((result, { startTime, endTime }) => {
      const startTimestamp = new Date(startTime).getTime()
      const endTimestamp = new Date(endTime).getTime()

      if (
        Number.isFinite(startTimestamp) &&
        Number.isFinite(endTimestamp) &&
        startTimestamp <= endTimestamp
      ) {
        result.push({
          startTime,
          endTime,
          startTimestamp,
          endTimestamp,
        })
      }

      return result
    }, [])
    .sort((a, b) => a.startTimestamp - b.startTimestamp)

  const union = []

  intervals.forEach((interval) => {
    const current = union[union.length - 1]

    if (!current) {
      union.push({ ...interval })
      return
    }

    // Gabungkan interval yang overlap atau menempel.
    if (interval.startTimestamp <= current.endTimestamp) {
      if (interval.endTimestamp > current.endTimestamp) {
        current.endTime = interval.endTime
        current.endTimestamp = interval.endTimestamp
      }

      return
    }

    union.push({ ...interval })
  })

  return union.map(({ startTime, endTime }) => ({
    startTime,
    endTime,
  }))
}

export const state = {
  vehicleTypes: [],
  vgroups: [],
  tempVehicleStatuses: {},
  vehicleStatuses: {},
  modalOpen: false,
  vehicleIdActive: null,
  tempVehicleId: null,
  period: [],
  videoChannelActive: [],
  loadingGetVehicles: false,
  loadingFilelist: false,
  loadingRawAlerts: false,
  mapFilelist: {}, // key is channel, value is filelist
  filelistUnion: [], // datetime in local date device
  mapFilelistUnionPerChannel: {}, // datetime in local date device
  rawAlerts: [],
  markerPositionTimeline: 0, // percentage
  activeTab: 'timeline', // timeline | filelist

  /**
   * key is normalizedChannels
   * value is
   *  'CONNECTING',
   *  'PLAYING',
   *  'ERROR',
   *  'FREEZE',
   *  'TIMEOUT',
   *  'ENDED',
   *  'SLEEP',
   *  'PAUSED'
   *
   * Those value is retrieve from webview video history,
   *
   * There is additional state to detect is webview has loaded or not
   *  'NOT_STARTED'
   *  'LOAD'
   *
   */
  mapWebviewStatePerChannel: {},
  mapWebviewDatetimePerChannel: {},
  mapWebviewTimestampPerChannel: {},

  /**
   * key is normalizedChannels
   * value is { start, end }
   * start and end is in UTC date
   */
  mapWebviewBufferedTimePerChannel: {},
  mapWebviewHiddenPausedActivePerChannel: {},

  hasIntentToTimeChange: false, // this value is true when user has been interact with timeline or bar and become false immedietely if iframe has been loaded
  filelistDownloadSelected: {
    startTime: null,
    endTime: null,
    channel: null,
  },
  filelistPlaySelected: {
    channel: null,
    mediaFilename: null,
    startTime: null, // UTC date
    endTime: null, // UTC date
  },

  /**
   * key is channel
   * value is { thumbUrl: string, isGeneratingThumbnail: boolean }
   */
  mapThumbnailUrlPerChannel: {},
  dateTimeIntentToChangeInFilelist: null, // utc date
}

export const getters = {
  vehicleIds(state) {
    return Object.keys(state.vehicleStatuses).map(Number)
  },
  deviceTypeActive(state) {
    const vs = state.vehicleStatuses[state.vehicleIdActive]
    if (!vs) return null
    return Helper.methods.getDeviceType(vs.vehicle.device.deviceCode)
  },
  statusTextActive(state) {
    const vs = state.vehicleStatuses[state.vehicleIdActive]
    if (!vs) return null
    return vs.statusText
  },
  deviceTimezone(state) {
    const vs = state.vehicleStatuses[state.vehicleIdActive]
    if (!vs) return null
    return vs.vehicle.device.timezone
  },
  normalizedChannels(state, getters) {
    const deviceType = getters.deviceTypeActive,
      statusText = getters.statusTextActive
    if (!deviceType) return []
    if (deviceType === 'jc') {
      return statusText.split(',').map((item) => Number(item) + 1)
    }
    return statusText.split(',').map((item) => Number(item))
  },
  periodInSeconds(state) {
    const [startTime, endTime] = state.period
    if (!startTime || !endTime) return 0
    return Math.max(
      0,
      Math.floor((new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000)
    )
  },
  markerTimelineDate(state) {
    const [startTime, endTime] = state.period
    if (!startTime || !endTime) return null
    const startTimestamp = new Date(startTime).getTime()
    const endTimestamp = new Date(endTime).getTime()

    if (
      !Number.isFinite(startTimestamp) ||
      !Number.isFinite(endTimestamp) ||
      endTimestamp <= startTimestamp
    ) {
      return null
    }

    const percentage = Math.min(Math.max(Number(state.markerPositionTimeline), 0), 100)
    const markerTimestamp = startTimestamp + (endTimestamp - startTimestamp) * (percentage / 100)
    return new Date(markerTimestamp).toISOString()
  },
  isThereIsWebviewActive(state) {
    return Object.values(state.mapWebviewStatePerChannel).some(
      (state) => state && state !== 'NOT_STARTED'
    )
  },
  checkThereIsFilelistInsideChannel(state, getters) {
    return (channel) => {
      const markerTimelineDate = getters.markerTimelineDate,
        deviceTimezone = getters.deviceTimezone
      const filelist = state.mapFilelist[channel]
      if (!filelist || filelist.length === 0) return false
      return filelist.some((file) => {
        const startTimeUtc = Helper.methods.getUtcDateFromLocalDate(file.startTime, deviceTimezone)
        const endTimeUtc = Helper.methods.getUtcDateFromLocalDate(file.endTime, deviceTimezone)
        return (
          Helper.methods.isBetweenDate(markerTimelineDate, startTimeUtc, endTimeUtc) ||
          new Date(startTimeUtc).getTime() === new Date(markerTimelineDate).getTime()
        )
      })
    }
  },
  bufferedTimeIntersection(state) {
    const buffers = Object.values(state.mapWebviewBufferedTimePerChannel)
      .map(({ start, end }) => ({
        start: new Date(start).getTime(),
        end: new Date(end).getTime(),
      }))
      .filter(({ start, end }) => Number.isFinite(start) && Number.isFinite(end) && end > start)

    if (buffers.length === 0) {
      return {
        start: null,
        end: null,
      }
    }

    const intersectionStart = Math.max(...buffers.map(({ start }) => start))
    const intersectionEnd = Math.min(...buffers.map(({ end }) => end))

    if (intersectionStart >= intersectionEnd) {
      return {
        start: null,
        end: null,
      }
    }

    return {
      start: new Date(intersectionStart).toISOString(),
      end: new Date(intersectionEnd).toISOString(),
    }
  },
  isMultiChannelActive(state) {
    return state.videoChannelActive.length > 1
  },
}

export const actions = {
  registerStreamIframe(_, { imei, channel, iframe }) {
    streamIframes.set(getStreamKey(imei, channel), iframe)
  },

  unregisterStreamIframe(_, { imei, channel }) {
    streamIframes.delete(getStreamKey(imei, channel))
  },

  postToStreamIframe(_, { imei, channel, payload }) {
    streamIframes
      .get(getStreamKey(imei, channel))
      ?.contentWindow?.postMessage(JSON.stringify(payload), '*')
  },

  async updateFileList({ state, getters }) {
    const deviceType = getters.deviceTypeActive
    if (!deviceType) return
    const { vehicleIdActive, period } = state
    if (!vehicleIdActive) return

    const input = { useInMemoryDB: true, imei: state.vehicleStatuses[vehicleIdActive].vehicle.imei }
    if (deviceType === 'bsj' || deviceType === 'vss-howen') {
      input['dateRange'] = { start: period[0], end: period[1] }
    }

    await Promise.all(
      getters.statusTextActive.split(',').map(async (c) => {
        let ch = Number(c)
        if (deviceType === 'jc') ch = ch + 1
        const payload = { ...input, channel: ch }
        await client.mutate({
          mutation: UPDATE_FILELIST,
          variables: {
            input: payload,
          },
          fetchPolicy: 'no-cache',
        })
      })
    )
  },

  setQueryParamsVideoHistory({ state }) {
    const { vehicleIdActive, period } = state
    const queryParams = new URLSearchParams(window.location.search)

    queryParams.set('vehicleId', vehicleIdActive)
    queryParams.set('startTime', period[0])
    queryParams.set('endTime', period[1])

    window.history.replaceState(null, '', `${window.location.pathname}?${queryParams.toString()}`)
  },

  async getAllContentVideoHistory({ commit, state, dispatch }, payload) {
    const fromReload = payload?.fromReload ?? false
    try {
      const { vehicleIdActive } = state
      commit('setLoadingFilelist', true)
      commit('resetMapFilelistUnionPerChannel')
      commit('resetFilelistUnion')
      commit('resetMapFilelistByChannel')
      commit('resetMapWebviewStatePerChannel')
      commit('resetMapWebviewDatetimePerChannel')
      commit('resetMapThumbnailUrlPerChannel')

      if (!fromReload) {
        commit('resetVideoChannelActive')
        commit('resetRawAlerts')
        dispatch('getRawAlerts')
        // TODO : Uncomment when ready
        // dispatch('getThumbnailPerChannel')
      }

      dispatch('setQueryParamsVideoHistory')

      await dispatch('updateFileList')

      await Helper.methods.delay(5000)

      const response = await client.query({
        query: GET_VEHICLE_VIDEOS_TV2,
        variables: {
          filter: {
            vehicleId: vehicleIdActive,
            startTime: state.period[0],
            endTime: state.period[1],
            fileListOnly: true,
            useInMemoryDB: true,
            isFileSplitted: false,
          },
        },
        fetchPolicy: 'no-cache',
      })
      const filelist = response.data.vehicleVideos.vehicleVideos.files

      const temp = {}
      filelist.forEach((file) => {
        const { channel } = file
        if (!temp[channel]) temp[channel] = []
        temp[channel].push(file)
      })

      Object.keys(temp).forEach((channel) => {
        commit('setMapFilelistByChannel', {
          channel,
          filelist: temp[channel],
        })
      })

      await dispatch('handleUnionForFilelist')
      dispatch('setChannelActiveDefault')
      await Vue.nextTick()
      dispatch('setMarkerPositionDefault')
    } catch (err) {
      console.error(err)
      mcToast.error(err?.message ?? 'Something went wrong')
    } finally {
      commit('setLoadingFilelist', false)
    }
  },

  setMarkerPositionDefault({ commit, state, getters }) {
    const [periodStart, periodEnd] = state.period
    if (state.filelistUnion.length === 0) return

    const startTime = state.filelistUnion[0]?.startTime
    const utcSelectedTime = Helper.methods.getUtcDateFromLocalDate(
      startTime,
      getters.deviceTimezone
    )

    const startTimestamp = moment.utc(periodStart).valueOf()
    const endTimestamp = moment.utc(periodEnd).valueOf()
    const selectedTimestamp = moment.utc(utcSelectedTime).valueOf()
    const duration = endTimestamp - startTimestamp
    const position = ((selectedTimestamp - startTimestamp) / duration) * 100

    commit('setMarkerPositionTimeline', Math.min(Math.max(position, 0), 100))
  },

  setChannelActiveDefault({ commit, getters, state }) {
    let channelActive = getters.normalizedChannels[0]

    for (const channel of getters.normalizedChannels) {
      if (state.mapFilelist[channel]?.length) {
        channelActive = channel
        break
      }
    }

    getters.normalizedChannels.forEach((channel) => {
      commit('setMapWebviewStatePerChannel', {
        channel,
        state: null,
      })
    })

    if (getters.checkThereIsFilelistInsideChannel(channelActive)) {
      commit('addVideoChannelActive', channelActive)
      commit('setMapWebviewStatePerChannel', {
        channel: channelActive,
        state: 'NOT_STARTED',
      })
    }
  },

  removeChannelToGridWebview({ commit }, channel) {
    commit('removeVideoChannelActive', channel)
    commit('setMapWebviewStatePerChannel', {
      channel: channel,
      state: null,
    })
  },

  async addChannelToGridWebview({ commit, state, getters }, channel) {
    commit('addVideoChannelActive', channel)
    await Vue.nextTick()

    const channelActive = Object.values(state.mapWebviewStatePerChannel).filter(
      (channelState) => channelState !== null
    )
    const isAllNotStarted = channelActive.every((channelState) => channelState === 'NOT_STARTED'),
      isHasChannelWebviewActive = channelActive.some(
        (channelState) => channelState !== 'NOT_STARTED'
      )

    if (!getters.checkThereIsFilelistInsideChannel(channel)) return

    if (isAllNotStarted) {
      commit('setMapWebviewStatePerChannel', {
        channel: channel,
        state: 'NOT_STARTED',
      })
    }

    if (isHasChannelWebviewActive) {
      commit('setMapWebviewStatePerChannel', {
        channel: channel,
        state: 'LOAD',
      })
    }
  },

  async playChannel({ commit }, channel) {
    commit('setMapWebviewStatePerChannel', {
      channel: channel,
      state: 'LOAD',
    })
    commit('setMapWebviewHiddenPausedActivePerChannel', {
      channel: channel,
      isHiddenPausedActive: false,
    })
  },

  async reloadFilelist({ dispatch }) {
    await dispatch('getAllContentVideoHistory', { fromReload: true })
  },

  async getRawAlerts({ commit, state }) {
    commit('setLoadingRawAlerts', true)
    try {
      const { vehicleIdActive } = state
      const adas = [90, 92, 91, 96, 113, 134],
        dms = [53, 52, 54, 55, 51, 56, 110, 50, 111, 112, 150, 148, 149, 152, 49],
        overspeed = [8],
        sharpTurn = [25],
        acceleration = [24, 23],
        inOutGeofence = [7],
        sos = [11],
        idleTime = [15],
        door = [4, 19, 5, 20],
        fuel = [13, 14]

      const alertCode = [
        ...adas,
        ...dms,
        ...overspeed,
        ...sharpTurn,
        ...acceleration,
        ...inOutGeofence,
        ...sos,
        ...idleTime,
        ...door,
        ...fuel,
      ]

      const response = await client.query({
        query: GET_RAW_ALERTS_VIDEO_HISTORY,
        variables: {
          filter: {
            vehicleId: vehicleIdActive,
            alertCode,
            startDate: state.period[0],
            endDate: state.period[1],
            orderBy: JSON.stringify({ createdOn: 'desc' }),
            pagination: {
              page: 1,
              show: 99999,
            },
          },
        },
        fetchPolicy: 'no-cache',
      })
      const alerts = response.data.rawAlerts.rawAlerts.data

      commit('setRawAlerts', alerts)
    } catch (err) {
      commit('setRawAlerts', [])
      console.error(err)
    } finally {
      commit('setLoadingRawAlerts', false)
    }
  },

  handleUnionForFilelist({ state, commit }) {
    commit('resetMapFilelistUnionPerChannel')

    // Tahap 1: buat union untuk setiap channel.
    Object.entries(state.mapFilelist).forEach(([channel, filelist]) => {
      const channelUnion = Array.isArray(filelist) ? mergeFilelistIntervals(filelist) : []

      commit('setMapFilelistUnionPerChannel', {
        channel,
        filelist: channelUnion,
      })
    })

    // Tahap 2: gabungkan seluruh union per channel.
    const allChannelUnions = Object.values(state.mapFilelistUnionPerChannel).reduce(
      (result, channelUnion) => {
        if (Array.isArray(channelUnion)) {
          result.push(...channelUnion)
        }

        return result
      },
      []
    )

    commit('setFilelistUnion', mergeFilelistIntervals(allChannelUnions))
  },

  async handleDownloadVideoHistory(
    { state, getters, dispatch, commit },
    /**
     * rangeStart and rangeEnd in format 'HH:mm:ss'
     */
    { channels, rangeStart, rangeEnd }
  ) {
    const dateActive = Helper.methods.convertUtcToLocalDate(state.period[0], getters.deviceTimezone)

    const _date = dateActive.split('T')[0]
    const localRangeStart = _date + 'T' + rangeStart,
      localRangeEnd = _date + 'T' + rangeEnd

    const utcRangeStart = Helper.methods.getUtcDateFromLocalDate(
        localRangeStart,
        getters.deviceTimezone
      ),
      utcRangeEnd = Helper.methods.getUtcDateFromLocalDate(localRangeEnd, getters.deviceTimezone)

    // await Helper.methods.delay(1000)
    const results = await Promise.all(
      channels.map(async (ch) => {
        const { responseMessage, dataDvh } = await dispatch('reqSetUploadMedia', {
          channel: ch,
          startTime: utcRangeStart,
          endTime: utcRangeEnd,
        })
        return { responseMessage, dataDvh }
      })
    )

    results.forEach(({ responseMessage, dataDvh }) => {
      const messageNoDownloaded = 'Not Sent(No video downloaded)'
      if (responseMessage === messageNoDownloaded) {
        mcToast.error(messageNoDownloaded)
        return
      }
      mcToast.success(i18n.t('videoHistory.processingDownloadVideo'))
      commit('vehicle/setShowWidgetDownload', true, { root: true })
      document.body.classList.add('mc-widget-download')
      dataDvh.forEach(async (d) => {
        const convertedChannel =
          getters.statusTextActive.split(',').findIndex((st) => {
            if (getters.deviceTypeActive === 'jc') {
              return Number(st) + 1 === Number(d.channel)
            }
            return Number(st) === Number(d.channel)
          }) + 1
        commit(
          'vehicle/addDvhPollingData',
          {
            downloadId: d.downloadId,
            data: {
              ...d,
              deviceTimezone: getters.deviceTimezone,
              licensePlate: state.vehicleStatuses[state.vehicleIdActive]?.vehicle?.licensePlate,
              convertedChannel,
            },
          },
          { root: true }
        )
      })
    })
  },

  async reqSetUploadMedia({ state }, { channel, startTime, endTime }) {
    const identifier = 'HistoricalVideo',
      useInMemoryDB = true,
      imei = state.vehicleStatuses[state.vehicleIdActive]?.vehicle?.imei

    const payload = { channel, startTime, endTime, identifier, useInMemoryDB, imei }

    const response = await client.mutate({
      mutation: SET_UPLOAD_MEDIA,
      variables: {
        input: payload,
      },
    })
    if (!response.data.setUploadMedia?.isSuccessful) {
      mcToast.error(response.data.setUploadMedia?.message ?? 'Download failed')
      return
    }
    const responseSetUploadMedia = response.data.setUploadMedia.setUploadMedia

    return {
      responseMessage: responseSetUploadMedia.message,
      dataDvh: responseSetUploadMedia.data,
    }
  },

  async getThumbnailPerChannel({ state, getters, commit }) {
    const channels = [...getters.normalizedChannels]
    const vehicleId = state.vehicleIdActive
    const promisesGetDvh = channels.map(async (ch) => {
      try {
        const response = await client.query({
          query: GET_THUMBNAIL_FOR_VIDEO_HISTORY,
          variables: {
            filter: {
              allUsers: true,
              channel: [ch],
              vehicleId,
              pagination: {
                page: 1,
                show: 1,
              },
              orderBy: [JSON.stringify({ startTime: 'desc' })],
              status: ['Success'],
              identifier: ['SystemVideo', 'Picture'],
            },
          },
          fetchPolicy: 'no-cache',
        })
        if (!response.data.downloadVideoHistories.isSuccessful) {
          return null
        }
        const dataDvh = response.data.downloadVideoHistories.downloadVideoHistories.data
        if (dataDvh.length === 0) {
          return null
        }
        return dataDvh[0]
      } catch (err) {
        return null
      }
    })

    const response = await Promise.all(promisesGetDvh)

    await Promise.all(
      response.map(async (res, index) => {
        const ch = channels[index]
        if (!res?.url || state.vehicleIdActive !== vehicleId) return
        if (res.identifier === 'P') {
          if (await isValidImageUrl(res.url)) {
            if (state.vehicleIdActive !== vehicleId) return
            commit('setMapThumbnailUrlPerChannelFromSource', { channel: ch, url: res.url })
          }
          return
        }

        const thumbUrl = Helper.methods.getThumbnailVideoUrl(res.url)
        const isValidThumbnail = await isValidImageUrl(thumbUrl)
        if (state.vehicleIdActive !== vehicleId) return

        if (isValidThumbnail) {
          commit('setMapThumbnailUrlPerChannelFromSource', { channel: ch, url: thumbUrl })
          return
        }

        const previousUrl = state.mapThumbnailUrlPerChannel[ch]?.thumbUrl ?? null
        let generatedUrl = null
        commit('setStartGeneratingThumbnail', { channel: ch })

        try {
          generatedUrl = await Helper.methods.generatePictureUrlFromVideo(res.url)
        } finally {
          if (state.vehicleIdActive === vehicleId) {
            commit('setMapThumbnailUrlPerChannelFromSource', {
              channel: ch,
              url: generatedUrl || previousUrl,
            })
          }
        }
      })
    )
  },
}

export const mutations = {
  setVehicleTypes(state, vehicleTypes) {
    state.vehicleTypes = vehicleTypes
  },
  setVgroups(state, vgroups) {
    state.vgroups = vgroups
  },
  setVehicleStatuses(state, payload) {
    state.vehicleStatuses = payload
  },
  resetTempVehicleStatuses(state) {
    state.tempVehicleStatuses = {}
  },
  setTempVehicleStatusesByVehicleId(
    state,
    { vehicleId, data: { calculatedMotionStatus, engineOn, lastReceive } }
  ) {
    Vue.set(state.tempVehicleStatuses, vehicleId, {
      ...state.tempVehicleStatuses[vehicleId],
      calculatedMotionStatus,
      engineOn,
      lastReceive,
    })
  },
  setVehicleStatusesByVehicleId(
    state,
    { vehicleId, data: { calculatedMotionStatus, engineOn, lastReceive } }
  ) {
    Vue.set(state.vehicleStatuses, vehicleId, {
      ...state.vehicleStatuses[vehicleId],
      calculatedMotionStatus,
      engineOn,
      lastReceive,
    })
  },
  setMapFilelistByChannel(state, { channel, filelist }) {
    Vue.set(state.mapFilelist, channel, filelist)
  },
  setModalOpen(state, modalOpen) {
    state.modalOpen = modalOpen
  },
  setVehicleIdActive(state, vehicleId) {
    state.vehicleIdActive = vehicleId
  },
  setPeriod(state, period) {
    state.period = period
  },
  setTempVehicleId(state, vehicleId) {
    state.tempVehicleId = vehicleId
  },
  addVideoChannelActive(state, videoChannel) {
    state.videoChannelActive = [...state.videoChannelActive, videoChannel]
  },
  removeVideoChannelActive(state, videoChannel) {
    state.videoChannelActive = state.videoChannelActive.filter(
      (channel) => channel !== videoChannel
    )
  },
  resetVideoChannelActive(state) {
    state.videoChannelActive = []
  },
  setLoadingGetVehicles(state, loading) {
    state.loadingGetVehicles = loading
  },
  setLoadingFilelist(state, loading) {
    state.loadingFilelist = loading
  },
  resetMapFilelistByChannel(state) {
    state.mapFilelist = {}
  },
  setFilelistUnion(state, filelistUnion) {
    state.filelistUnion = filelistUnion
  },
  resetFilelistUnion(state) {
    state.filelistUnion = []
  },
  setRawAlerts(state, rawAlerts) {
    state.rawAlerts = rawAlerts
  },
  resetRawAlerts(state) {
    state.rawAlerts = []
  },
  setMapWebviewStatePerChannel(state, { channel, state: _state }) {
    Vue.set(state.mapWebviewStatePerChannel, channel, _state)
  },
  resetMapWebviewStatePerChannel(state) {
    state.mapWebviewStatePerChannel = {}
  },
  setMarkerPositionTimeline(state, markerPositionTimeline) {
    state.markerPositionTimeline = markerPositionTimeline
  },
  setMapWebviewDatetimePerChannel(state, { channel, datetime }) {
    Vue.set(state.mapWebviewDatetimePerChannel, channel, datetime)
  },
  resetMapWebviewDatetimePerChannel(state) {
    state.mapWebviewDatetimePerChannel = {}
  },
  setHasIntentToTimeChange(state, hasIntentToTimeChange) {
    state.hasIntentToTimeChange = hasIntentToTimeChange
  },
  setFilelistDownloadSelected(state, { startTime, endTime, channel }) {
    Vue.set(state.filelistDownloadSelected, 'startTime', startTime)
    Vue.set(state.filelistDownloadSelected, 'endTime', endTime)
    Vue.set(state.filelistDownloadSelected, 'channel', channel)
  },
  resetFilelistDownloadSelected(state) {
    Vue.set(state.filelistDownloadSelected, 'startTime', null)
    Vue.set(state.filelistDownloadSelected, 'endTime', null)
    Vue.set(state.filelistDownloadSelected, 'channel', null)
  },
  setMapWebviewBufferedTimePerChannel(state, { channel, start, end }) {
    Vue.set(state.mapWebviewBufferedTimePerChannel, channel, { start, end })
  },
  deleteMapWebviewBufferedTimePerChannel(state, channel) {
    Vue.delete(state.mapWebviewBufferedTimePerChannel, channel)
  },
  resetMapWebviewBufferedTimePerChannel(state) {
    state.mapWebviewBufferedTimePerChannel = {}
  },
  setActiveTab(state, activeTab) {
    state.activeTab = activeTab
  },
  setFilelistPlaySelected(state, { startTime, endTime, channel, mediaFilename }) {
    Vue.set(state.filelistPlaySelected, 'startTime', startTime)
    Vue.set(state.filelistPlaySelected, 'endTime', endTime)
    Vue.set(state.filelistPlaySelected, 'channel', channel)
    Vue.set(state.filelistPlaySelected, 'mediaFilename', mediaFilename)
  },
  resetFilelistPlaySelected(state) {
    Vue.set(state.filelistPlaySelected, 'startTime', null)
    Vue.set(state.filelistPlaySelected, 'endTime', null)
    Vue.set(state.filelistPlaySelected, 'channel', null)
    Vue.set(state.filelistPlaySelected, 'mediaFilename', null)
  },
  setMapFilelistUnionPerChannel(state, { channel, filelist }) {
    Vue.set(state.mapFilelistUnionPerChannel, channel, filelist)
  },
  resetMapFilelistUnionPerChannel(state) {
    Vue.set(state, 'mapFilelistUnionPerChannel', {})
  },
  setLoadingRawAlerts(state, loading) {
    state.loadingRawAlerts = loading
  },
  setMapWebviewHiddenPausedActivePerChannel(state, { channel, isHiddenPausedActive }) {
    Vue.set(state.mapWebviewHiddenPausedActivePerChannel, channel, isHiddenPausedActive)
  },
  resetMapWebviewHiddenPausedActivePerChannel(state) {
    Vue.set(state, 'mapWebviewHiddenPausedActivePerChannel', {})
  },
  setMapWebviewTimestampPerChannel(state, { channel, timestamp }) {
    Vue.set(state.mapWebviewTimestampPerChannel, channel, timestamp)
  },
  resetMapWebviewTimestampPerChannel(state) {
    Vue.set(state, 'mapWebviewTimestampPerChannel', {})
  },
  setMapThumbnailUrlPerChannelFromSource(state, { channel, url }) {
    Vue.set(state.mapThumbnailUrlPerChannel, channel, {
      thumbUrl: url,
      isGeneratingThumbnail: false,
    })
  },
  setStartGeneratingThumbnail(state, { channel }) {
    const current = state.mapThumbnailUrlPerChannel[channel]

    Vue.set(state.mapThumbnailUrlPerChannel, channel, {
      ...current,
      isGeneratingThumbnail: true,
    })
  },
  resetMapThumbnailUrlPerChannel(state) {
    Vue.set(state, 'mapThumbnailUrlPerChannel', {})
  },
  setDateTimeIntentToChangeInFilelist(state, dateTime) {
    state.dateTimeIntentToChangeInFilelist = dateTime
  },
}
